import { Box, Collapse, List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import ArticleIcon from "@mui/icons-material/Article";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CampaignIcon from "@mui/icons-material/Campaign";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GroupsIcon from "@mui/icons-material/Groups";
import MailIcon from "@mui/icons-material/Mail";
import PeopleIcon from "@mui/icons-material/People";
import PermMediaIcon from "@mui/icons-material/PermMedia";
import SecurityIcon from "@mui/icons-material/Security";
import ListAltIcon from "@mui/icons-material/ListAlt";
import type { ReactNode } from "react";
import { useState } from "react";
import { NavLink, useLocation } from "react-router";
import { useAuth } from "../auth/useAuth";
import { NAV } from "../app/navigation";
import { sidebar } from "../app/theme";

const ICONS: Record<string, ReactNode> = {
  survey: <AssignmentIcon fontSize="small" />,
  newsfeed: <ArticleIcon fontSize="small" />,
  clergy: <GroupsIcon fontSize="small" />,
  users: <PeopleIcon fontSize="small" />,
  roles: <SecurityIcon fontSize="small" />,
  registration: <ListAltIcon fontSize="small" />,
  notification: <CampaignIcon fontSize="small" />,
  "content-list": <PermMediaIcon fontSize="small" />,
  templates: <MailIcon fontSize="small" />,
  "calendar-management": <EventAvailableIcon fontSize="small" />,
};

const itemStyle = {
  color: sidebar.text,
  "& .MuiListItemIcon-root": { color: sidebar.mutedText, minWidth: 36 },
  "&.active": {
    bgcolor: "rgba(255,255,255,0.14)",
    borderLeft: `4px solid ${sidebar.accent}`,
    pl: 1.5,
    "& .MuiListItemIcon-root": { color: sidebar.text },
    "& .MuiListItemText-primary": { fontWeight: 600 },
  },
};

/**
 * Authority-filtered navigation: a link shows when the user holds ANY of the
 * entry's authorities, or when the entry is ungated.
 */
export function Sidebar() {
  const { canAny } = useAuth();
  const location = useLocation();
  const [templatesOpen, setTemplatesOpen] = useState(() =>
    location.pathname.includes("/templates"),
  );

  const visible = NAV.filter(
    (entry) => entry.inSidebar && (entry.authorities.length === 0 || canAny(entry.authorities)),
  );

  return (
    <Box
      component="nav"
      aria-label="Main"
      sx={{
        width: sidebar.width,
        flexShrink: 0,
        bgcolor: sidebar.background,
        color: sidebar.text,
      }}
    >
      <Box sx={{ px: 2, py: 2.5 }}>
        <Box component="span" sx={{ fontSize: 18, fontWeight: 600, color: sidebar.text }}>
          Assembly of Bishops
        </Box>
      </Box>
      <List>
        {visible.map((entry) =>
          entry.children ? (
            <Box key={entry.path}>
              <ListItemButton
                onClick={() => setTemplatesOpen((open) => !open)}
                aria-expanded={templatesOpen}
                sx={itemStyle}
              >
                <ListItemIcon>{ICONS[entry.path]}</ListItemIcon>
                <ListItemText primary={entry.label} />
                {templatesOpen ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </ListItemButton>
              <Collapse in={templatesOpen} unmountOnExit>
                <List disablePadding>
                  {entry.children.map((child) => (
                    <ListItemButton
                      key={child.path}
                      component={NavLink}
                      to={`/dashboard/${entry.path}/${child.path}`}
                      sx={{ ...itemStyle, pl: 5.5, color: sidebar.mutedText }}
                    >
                      <ListItemText primary={child.label} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </Box>
          ) : (
            <ListItemButton
              key={entry.path}
              component={NavLink}
              to={`/dashboard/${entry.path}`}
              sx={itemStyle}
            >
              <ListItemIcon>{ICONS[entry.path]}</ListItemIcon>
              <ListItemText primary={entry.label} />
            </ListItemButton>
          ),
        )}
      </List>
    </Box>
  );
}
